import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { useProfile } from '@/lib/hooks/use-profile'
import { useOwnFreelancerProfile } from '@/lib/hooks/use-own-freelancer-profile'
import { useOwnClientProfile } from '@/lib/hooks/use-own-client-profile'
import { AnimatedLoader } from '@/components/ui/animated-loader'
import { toast } from 'sonner'
import type { Database } from '@/lib/supabase/supabase-types'
import { emailService } from '@/lib/api/services/email.service'
import { RoleSelectionStep } from '@/components/onboarding/RoleSelectionStep'
import { BasicInfoStep } from '@/components/onboarding/BasicInfoStep'
import { CompleteStep } from '@/components/onboarding/CompleteStep'

type OnboardingStep = 'role' | 'basic-info' | 'complete'

export interface OnboardingFormData {
  is_genius: boolean
  is_client: boolean
  // Genius/Freelancer fields (for freelancer_profiles table)
  display_name: string
  headline: string
  freelancer_bio: string
  skills: string[]
  linkedin_url: string
  website_url: string
  location: string
  hourly_rate: string
  is_searchable: boolean
  freelancer_email: string
  profile_picture_url: string | null
  video_intro_url: string
  // Client fields (for client_profiles table)
  company_name: string
  company_size: string
  industry: string
  client_website_url: string
  client_bio: string
  client_location: string
  client_email: string
}

export default function Onboarding() {
  const navigate = useNavigate()
  const { user, isLoaded: isUserLoaded } = useUser()
  const { profile, updateProfileAsync } = useProfile()
  const { saveFreelancerProfile } = useOwnFreelancerProfile()
  const { saveClientProfile } = useOwnClientProfile()
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('role')
  const [selectedRole, setSelectedRole] = useState<'genius' | 'client' | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Extract user data from Clerk
  const getUserEmail = () => user?.primaryEmailAddress?.emailAddress || ''

  const [formData, setFormData] = useState<OnboardingFormData>({
    is_genius: false,
    is_client: false,
    // Genius/Freelancer fields
    display_name: '',
    headline: '',
    freelancer_bio: '',
    skills: [],
    linkedin_url: '',
    website_url: '',
    location: '',
    hourly_rate: '',
    is_searchable: true,
    freelancer_email: '',
    profile_picture_url: null,
    video_intro_url: '',
    // Client fields
    company_name: '',
    company_size: '',
    industry: '',
    client_website_url: '',
    client_bio: '',
    client_location: '',
    client_email: '',
  })

  // Pre-populate form data with Clerk user information when user data loads
  useEffect(() => {
    if (isUserLoaded && user) {
      const userEmail = user?.primaryEmailAddress?.emailAddress || ''
      const userFirstName = user.firstName || ''
      const userLastName = user.lastName || ''
      const userDisplayName = user.fullName || [userFirstName, userLastName].filter(Boolean).join(' ') || ''

      setFormData(prev => ({
        ...prev,
        display_name: userDisplayName,
        freelancer_email: userEmail,
        client_email: userEmail,
      }))
    }
     
  }, [isUserLoaded, user])

  // Memoize the callback to prevent infinite loops
  const handleImageUploaded = useCallback((imageUrl: string) => {
    setFormData((prev) => {
      // Only update if the URL actually changed
      if (prev.profile_picture_url === imageUrl) {
        return prev
      }
      return { ...prev, profile_picture_url: imageUrl }
    })
  }, [])

  // Handle image removal during onboarding
  const handleImageRemoved = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      profile_picture_url: null
    }))
  }, [])

  if (!profile || !isUserLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <AnimatedLoader message="Loading..." />
      </div>
    )
  }

  const selectRole = (role: 'genius' | 'client') => {
    setSelectedRole(role)
    const updatedFormData = {
      ...formData,
      is_genius: role === 'genius',
      is_client: role === 'client',
    }
    setFormData(updatedFormData)
    // Move to basic info step instead of completing immediately
    setCurrentStep('basic-info')
  }

  const handleComplete = async (role: 'genius' | 'client', data: OnboardingFormData) => {
    // Prepare user profile update data
    const userUpdates: Database['public']['Tables']['users']['Update'] = {
      is_genius: role === 'genius',
      is_client: role === 'client',
      onboarding_completed: true,
    }

      // Create freelancer profile with basic info from form
      if (role === 'genius' && saveFreelancerProfile) {
        // Use first name + last name as display name (required fields from sign-up)
        const firstName = user?.firstName || ''
        const lastName = user?.lastName || ''
        const displayName = [firstName, lastName].filter(Boolean).join(' ').trim() || data.display_name.trim()
        
        await saveFreelancerProfile({
          display_name: displayName,
          headline: data.headline.trim() || null,
          bio: data.freelancer_bio.trim() || null,
          skills: null,
          linkedin_url: null,
          website_url: null,
          location: data.location?.trim() || null,
          hourly_rate_cents: null,
          is_searchable: true,
          profile_picture_url: data.profile_picture_url || null,
          email: data.freelancer_email.trim() || getUserEmail() || null,
          // video_intro_urls exists in DB but not in generated types yet
          video_intro_urls: data.video_intro_url?.trim() ? [data.video_intro_url.trim()] : [],
        } as Parameters<typeof saveFreelancerProfile>[0], { suppressToast: true })
      }

      // Create client profile with basic info from form
      if (role === 'client' && saveClientProfile) {
        // Map company size from UI value to database value
        const mapCompanySizeToDb = (size: string | undefined): string | null => {
          if (!size) return null;
          const sizeMap: Record<string, string> = {
            'startup': '1-10',
            'small': '11-50',
            'medium': '51-200',
            'enterprise': '200+',
          };
          return sizeMap[size] || null;
        };

        await saveClientProfile({
          company_name: data.company_name.trim(),
          company_size: mapCompanySizeToDb(data.company_size) || null,
          industry: data.industry?.trim() || null,
          location: data.client_location?.trim() || null,
          bio: data.client_bio.trim() || null,
          website_url: data.client_website_url?.trim() || null,
          profile_picture_url: data.profile_picture_url || null,
          email: data.client_email.trim() || getUserEmail() || null,
        }, { suppressToast: true })
      }

    // Update user profile - this will trigger role context updates
    if (updateProfileAsync) {
      await updateProfileAsync(userUpdates, { suppressToast: true })
    }

    // Send onboarding email with role-specific next steps (non-blocking)
    const userEmail = getUserEmail()
    if (userEmail) {
      emailService.sendOnboardingRoleSelectedEmail(userEmail, role).catch((error) => {
        console.error('Failed to send onboarding email:', error)
        // Don't show error to user, email sending is non-critical
      })
    }

    // Show success message
    toast.success('Welcome to AI Expert Network!', {
      description: 'Your account has been created. Complete your profile to get started.',
    })

    // Navigate to the appropriate dashboard after a short delay
    setTimeout(() => {
      navigate(role === 'client' ? '/client/dashboard' : '/genius/dashboard', { replace: true })
    }, 1500)
  }

  const handleBasicInfoSubmit = async () => {
    if (!selectedRole || isSubmitting) return

    // Validate required fields
    if (selectedRole === 'client') {
      if (!formData.company_name.trim()) {
        toast.error('Company name is required')
        return
      }
      if (!formData.client_bio.trim()) {
        toast.error('Company bio is required')
        return
      }
      if (formData.client_bio.trim().length < 100) {
        toast.error('Company bio must be at least 100 characters')
        return
      }
      if (formData.client_bio.trim().length > 1500) {
        toast.error('Company bio must not exceed 1500 characters')
        return
      }
    } else {
      // For freelancers, validate headline, location and bio are required
      if (!formData.headline.trim()) {
        toast.error('Headline is required')
        return
      }
      if (!formData.location.trim()) {
        toast.error('Location is required')
        return
      }
      if (!formData.freelancer_bio.trim()) {
        toast.error('Bio is required')
        return
      }
      if (formData.freelancer_bio.trim().length < 100) {
        toast.error('Bio must be at least 100 characters')
        return
      }
      if (formData.freelancer_bio.trim().length > 1500) {
        toast.error('Bio must not exceed 1500 characters')
        return
      }
      if (!formData.video_intro_url?.trim()) {
        toast.error('Video introduction URL is required')
        return
      }
    }

    setIsSubmitting(true)
    setCurrentStep('complete')
    
    try {
      // Save everything
      await handleComplete(selectedRole, formData)
    } catch (_error) {
      setIsSubmitting(false)
      setCurrentStep('basic-info')
    }
  }


  // Basic Info Step
  if (currentStep === 'basic-info' && selectedRole) {
    return (
      <BasicInfoStep
        selectedRole={selectedRole}
        user={user}
        formData={formData}
        isSubmitting={isSubmitting}
        onBack={() => {
          setCurrentStep('role')
          setSelectedRole(null)
        }}
        onCompanyNameChange={(value) => setFormData({ ...formData, company_name: value })}
        onClientBioChange={(value) => setFormData({ ...formData, client_bio: value })}
        onCompanySizeChange={(value) => setFormData({ ...formData, company_size: value })}
        onIndustryChange={(value) => setFormData({ ...formData, industry: value })}
        onClientLocationChange={(value) => setFormData({ ...formData, client_location: value })}
        onWebsiteUrlChange={(value) => setFormData({ ...formData, client_website_url: value })}
        onHeadlineChange={(value) => setFormData({ ...formData, headline: value })}
        onFreelancerBioChange={(value) => setFormData({ ...formData, freelancer_bio: value })}
        onLocationChange={(value) => setFormData({ ...formData, location: value })}
        onVideoIntroUrlChange={(value) => setFormData({ ...formData, video_intro_url: value })}
        onImageUploaded={handleImageUploaded}
        onImageRemoved={handleImageRemoved}
        onSubmit={handleBasicInfoSubmit}
      />
    )
  }

  // Role Selection Step
  if (currentStep === 'role') {
    return <RoleSelectionStep onSelectRole={selectRole} />
  }

  // Complete Step - shows while setting up account
  return <CompleteStep selectedRole={selectedRole} />
}
